<template>
  <a-popover
    v-if="canEdit"
    trigger="click"
  >
    <template #content>
      <a-spin :spinning="isLoading">
        <a-space direction="vertical">
          <a-date-picker
            v-model:value="startDay"
            class="date-picker"
            :disabled-date="disabledDateForStartDay"
            @change="onSave"
          />
          <a-date-picker
            v-model:value="finishDay"
            class="date-picker"
            :disabled-date="disabledDateForFinishDay"
            @change="onSave"
          />
          <div class="bottom">
            <a-tooltip>
              <template #title>
                {{ $t('okr.autoSaveOnBlur') }}
              </template>
              <a-input-number
                v-model:value="form.progress"
                :min="0"
                :max="100"
                :step="5"
                :formatter="value => `${value}%`"
                :parser="value => value.replace('%', '')"
                @blur="e => onSave()"
              />
            </a-tooltip>
            <a-checkbox
              v-model:checked="giveUpStatus"
              @change="onSave"
            >
              {{ $t(`okr.stateOptions.${3}`) }}
            </a-checkbox>
          </div>
        </a-space>
      </a-spin>
    </template>
    <CircularProgress
      :progress="form.progress"
      :radius="7"
      :stroke-width="3"
      :stroke-color="stateMap[form.stateId.id]"
      class="clickable key-result-progress"
    />
  </a-popover>
  <CircularProgress
    v-else
    :progress="form.progress"
    :radius="7"
    :stroke-width="3"
    :stroke-color="stateMap[form.stateId.id]"
  />
</template>

<script>
import { ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'

import STATE_OPTIONS from '@/constants/state-options'
import { updateKeyResult } from '@/api/okr'
import CircularProgress from '@/components/CircularProgress'
import { calculateStatus } from '@/utils/calculateStatus'
import moment from 'moment'

const STATE_MAP = STATE_OPTIONS.reduce((obj, state) => {
  obj[state.value] = state.color
  return obj
}, {})

export default {
  components: {
    CircularProgress
  },
  props: {
    canEdit: {
      type: Boolean,
      required: true
    },
    keyResult: {
      type: Object,
      required: true
    }
  },
  emits: ['progress-updated'],
  setup (props, { emit }) {
    const { t } = useI18n()
    const form = toRef(props, 'keyResult')
    const isLoading = ref(false)
    const giveUpStatus = ref(form.value.stateId.id === 4)

    const startDay = ref(
      form.value.startDay ? moment.unix(form.value.startDay) : undefined
    )
    const finishDay = ref(
      form.value.finishDay ? moment.unix(form.value.finishDay) : undefined
    )

    watch(form, val => {
      startDay.value = form.value.startDay ? moment.unix(form.value.startDay) : undefined
      finishDay.value = form.value.finishDay ? moment.unix(form.value.finishDay) : undefined
      giveUpStatus.value = form.value.stateId.id === 4
    })

    function getStatus () {
      if (giveUpStatus.value) {
        return 4
      } else {
        return calculateStatus(
          startDay.value,
          finishDay.value,
          form.value.progress
        ) || form.value.stateId.id
      }
    }

    function onSave () {
      isLoading.value = true
      updateKeyResult(form.value.id, {
        progress: form.value.progress,
        start_day: startDay.value?.unix() || 0,
        finish_day: finishDay.value?.unix() || 0,
        state_id: getStatus()
      })
        .then(() => {
          message.success(t('okr.progressDialog.progressSuccessMessage'))
          emit('progress-updated')
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    function disabledDateForFinishDay (date) {
      return startDay.value && date.isBefore(startDay.value)
    }

    function disabledDateForStartDay (date) {
      return finishDay.value && date.isAfter(finishDay.value)
    }

    return {
      isLoading,
      form,
      startDay,
      finishDay,
      stateOptions: STATE_OPTIONS,
      stateMap: STATE_MAP,
      giveUpStatus,
      onSave,
      disabledDateForFinishDay,
      disabledDateForStartDay
    }
  }
}
</script>

<style lang="less" scoped>
.state-option {
  display: block;
  height: 30px;
  line-height: 30px;

  :deep(.ant-radio-inner) {
    border-color: var(--color) !important;

    &:after {
      background-color: var(--color) !important;
    }
  }
}

.date-picker {
  width: 100%;
}

.bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
